import {
	type IntakeDraft,
	type IntakeSectionKey,
} from '#app/routes/mocra+/_form+/generate/intake-loader.ts'
import { createSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'
import { type SectionKey as MoCRASectionKey } from '#app/routes/mocra+/_sections+/section-data.ts'

export type SectionMeta = {
	readonly key: IntakeSectionKey
	readonly label: string
	readonly route: string
}

export const ALL_MOCRA_SECTION_KEYS: readonly MoCRASectionKey[] = [
	'overview',
	'605',
	'606',
	'607',
	'608',
	'609',
	'610',
	'611',
	'612',
] as const

export const GENERATE_SECTION_RESOURCES = createSectionResources({
	mocraSections: ALL_MOCRA_SECTION_KEYS,
	templateSections: ALL_MOCRA_SECTION_KEYS,
})

export const SECTION_METADATA: readonly SectionMeta[] = [
	{ key: 'company', label: 'Company', route: '/mocra/company' },
	{
		key: 'primaryContacts',
		label: 'Primary Contacts',
		route: '/mocra/primary-contacts',
	},
	{ key: 'products', label: 'Products', route: '/mocra/products' },
	{ key: 'facilities', label: 'Facilities', route: '/mocra/facilities' },
	{
		key: 'adverseEvents',
		label: 'Adverse Event Ops',
		route: '/mocra/adverse-events',
	},
	{
		key: 'suppliersTesting',
		label: 'Suppliers & Testing',
		route: '/mocra/suppliers-testing',
	},
	{
		key: 'labelGovernance',
		label: 'Label Governance',
		route: '/mocra/label-governance',
	},
	{
		key: 'recordsAccess',
		label: 'Records Access',
		route: '/mocra/records-access',
	},
	{
		key: 'recallPreparedness',
		label: 'Recall Preparedness',
		route: '/mocra/recall-preparedness',
	},
	{
		key: 'trainingDocumentation',
		label: 'Training & Docs',
		route: '/mocra/training-documentation',
	},
	{
		key: 'regulatoryMonitoring',
		label: 'Regulatory Monitoring',
		route: '/mocra/regulatory-monitoring',
	},
	{
		key: 'policiesReferences',
		label: 'Policies & References',
		route: '/mocra/policies-references',
	},
] as const

export type SectionStatus = SectionMeta & {
	readonly complete: boolean
	readonly missingFields: readonly string[]
}

export function computeSectionStatuses(draft: IntakeDraft): SectionStatus[] {
	return SECTION_METADATA.map((section) => {
		const data = draft[section.key]
		const record = data as Record<string, unknown> | undefined
		return {
			...section,
			complete: isSectionComplete(record),
			missingFields: listMissingFields(record),
		}
	})
}

function isSectionComplete(section: Record<string, unknown> | undefined) {
	if (!section) return false
	const entries = Object.values(section)
	if (!entries.length) return false
	return entries.every((value) => {
		if (typeof value === 'string') return value.trim().length > 0
		return value !== null && value !== undefined
	})
}

function listMissingFields(section: Record<string, unknown> | undefined) {
	if (!section) return []
	const missing: string[] = []
	for (const [key, value] of Object.entries(section)) {
		if (typeof value === 'string') {
			if (!value.trim()) missing.push(humanizeKey(key))
		} else if (value === null || value === undefined) {
			missing.push(humanizeKey(key))
		}
	}
	return missing
}

function humanizeKey(key: string) {
	return key
		.replace(/([A-Z])/g, ' $1')
		.replace(/_/g, ' ')
		.replace(/\s{2,}/g, ' ')
		.replace(/^\w/g, (char) => char.toUpperCase())
		.trim()
}
